import type { ReactNode } from 'react';
import { stagger } from '@/lib/motion';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { cn } from '@/lib/utils';
import { RowActions, type RowAction } from '@/components/RowActions';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export interface CompactColumn<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Body cells only (e.g. `max-w-0` so long values truncate instead of widening the column). */
  className?: string;
  /** Header cell only: the column's width (`w-[40%]`). */
  width?: string;
  /** Container-query class that hides the column in a narrow card (its facts move into another cell), e.g. `@max-[40rem]:hidden`. */
  hide?: string;
}

interface Props<T> {
  label: string;
  rows: T[];
  columns: CompactColumn<T>[];
  rowKey: (row: T) => string;
  rowTestId?: (row: T) => string;
  /** The row's name for accessible action labels. */
  subject: (row: T) => string;
  actions?: (row: T) => RowAction[];
  actionsVariant?: 'text' | 'icon' | 'menu';
  /** Phones: one stacked card per row instead of the table. */
  phoneRow: (row: T) => ReactNode;
  /** Highlights the row the page is about (the selected project or app). */
  selected?: (row: T) => boolean;
  className?: string;
}

/** Phones: one stacked card per row. */
function PhoneRow<T>({ row, index, ...p }: Props<T> & { row: T; index: number }) {
  const rowActions = p.actions?.(row) ?? [];
  const isSelected = Boolean(p.selected?.(row));
  return (
    <li
      data-testid={p.rowTestId?.(row)}
      data-selected={isSelected || undefined}
      style={stagger(index)}
      className={cn('enter flex min-w-0 items-start gap-2 rounded-lg border bg-card p-3 dark:bg-background', isSelected && 'border-border-strong shadow-[inset_2px_0_0_var(--foreground)] dark:bg-secondary/60 dark:shadow-none')}
    >
      <div className="min-w-0 flex-1">{p.phoneRow(row)}</div>
      {rowActions.length ? <RowActions actions={rowActions} subject={p.subject(row)} className="-my-1 -mr-1" /> : null}
    </li>
  );
}

function PhoneList<T>(p: Props<T>) {
  return (
    <ul aria-label={p.label} className={cn('flex flex-col gap-2', p.className)}>
      {p.rows.map((row, i) => (
        <PhoneRow key={p.rowKey(row)} row={row} index={i} {...p} />
      ))}
    </ul>
  );
}

function TableHeaderRow<T>({ columns, actions }: Pick<Props<T>, 'columns' | 'actions'>) {
  return (
    <TableHeader className="bg-secondary/50">
      <TableRow className="hover:bg-transparent">
        {columns.map((c) => (
          <TableHead key={c.key} className={cn(c.width, c.hide)}>
            {c.header}
          </TableHead>
        ))}
        {actions ? (
          <TableHead className="w-0 text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        ) : null}
      </TableRow>
    </TableHeader>
  );
}

function TableBodyRow<T>({ row, index, ...p }: Props<T> & { row: T; index: number }) {
  return (
    <TableRow data-testid={p.rowTestId?.(row)} data-selected={p.selected?.(row) || undefined} style={stagger(index)} className={cn('enter', p.selected?.(row) && 'bg-secondary/60')}>
      {p.columns.map((c) => (
        <TableCell key={c.key} className={cn('align-top', c.className, c.hide)}>
          {c.cell(row)}
        </TableCell>
      ))}
      {p.actions ? (
        <TableCell className="w-0 text-right align-top">
          <RowActions actions={p.actions(row)} subject={p.subject(row)} variant={p.actionsVariant ?? 'text'} className="-my-1" />
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function DesktopTable<T>(p: Props<T>) {
  return (
    <div className={cn('@container min-w-0 overflow-hidden rounded-lg border', p.className)}>
      <Table aria-label={p.label} className="[&_td]:px-3 [&_td]:py-2 [&_th]:h-9 [&_th]:px-3 sm:[&_td:first-child]:pl-4 sm:[&_th:first-child]:pl-4 sm:[&_td:last-child]:pr-3 sm:[&_th:last-child]:pr-3">
        <TableHeaderRow columns={p.columns} actions={p.actions} />
        <TableBody>
          {p.rows.map((row, i) => (
            <TableBodyRow key={p.rowKey(row)} row={row} index={i} {...p} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * The compact tables of the setup cards: a hairline-bordered table whose columns fold by container width, row
 * actions at the end, and one stacked card per row on phones. Rows enter staggered (styles/motion.css).
 */
export function CompactTable<T>(props: Props<T>) {
  const phone = useBreakpoint() === 'phone';
  return phone ? <PhoneList {...props} /> : <DesktopTable {...props} />;
}
